/**
 * Shared helpers for service topology editors (VPN / Database / RDS / …).
 * Provide:
 *   - small list editor (rows with N inputs + delete + add)
 *   - server picker (combo loaded from candidates)
 *   - location picker
 *   - palette / colour constants
 */
import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

export const TOPOLOGY_PALETTE = {
  cardBg:     'var(--bg-card)',
  cardBorder: '1px solid var(--slate-300)',
  subtleBg:   'var(--bg-input)',
  textMuted:  'var(--text-muted)',
  textStrong: 'var(--text-primary)',

  haAccent:        '#f59e0b',
  primaryAccent:   '#3b82f6',
  secondaryAccent: '#a78bfa',
  successAccent:   '#10b981',
  dangerAccent:    '#ef4444',
};

export function useTopologyCandidates(serviceId, companyId) {
  const [servers,   setServers]   = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (!serviceId) return;
    api.getServiceTopologyServerCandidates(serviceId, companyId)
      .then(r => setServers(r.candidates || []))
      .catch(() => setServers([]));
    api.getServiceTopologyLocationCandidates(serviceId, companyId)
      .then(r => setLocations(r.candidates || []))
      .catch(() => setLocations([]));
  }, [serviceId, companyId]);

  return { servers, locations };
}

export function ServerPicker({ value, onChange, servers, placeholder }) {
  return (
    <select
      className="form-select form-select-sm"
      value={value || ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">{placeholder || '— Sin servidor asociado —'}</option>
      {(servers || []).map(s => (
        <option key={`${s.kind}-${s.id}`} value={s.id}>
          {s.hostname || s.name}{s.ip ? ` · ${s.ip}` : ''}{s.kind === 'vm' ? ' (VM)' : ''}
        </option>
      ))}
    </select>
  );
}

export function LocationPicker({ value, onChange, locations, placeholder }) {
  return (
    <select
      className="form-select form-select-sm"
      value={value || ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">{placeholder || '— Sin sede —'}</option>
      {(locations || []).map(l => (
        <option key={l.id} value={l.id}>
          {l.name}{l.code ? ` (${l.code})` : ''}
        </option>
      ))}
    </select>
  );
}

/**
 * Generic editable rows list.
 *   columns: [{ key, label, type?, options?, placeholder?, width? }]
 *   type: 'text' (default) | 'select' | 'server' | 'location' | 'checkbox' | 'number'
 */
export function RowEditor({
  items, onChange, columns, editMode,
  servers, locations, addLabel = 'Añadir', emptyText = 'Sin entradas',
  defaultRow = {},
}) {
  const addRow = () => onChange([...(items || []), { ...defaultRow }]);
  const removeRow = (idx) => {
    const next = (items || []).slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const updateRow = (idx, key, value) => {
    const next = (items || []).slice();
    next[idx] = { ...(next[idx] || {}), [key]: value };
    onChange(next);
  };

  const renderInput = (col, row, idx) => {
    const val = row[col.key];
    if (col.type === 'select') {
      return (
        <select
          className="form-select form-select-sm"
          value={val || ''}
          onChange={e => updateRow(idx, col.key, e.target.value)}
        >
          <option value="">—</option>
          {(col.options || []).map(o => {
            const optVal = typeof o === 'string' ? o : o.value;
            const optLbl = typeof o === 'string' ? o : o.label;
            return <option key={optVal} value={optVal}>{optLbl}</option>;
          })}
        </select>
      );
    }
    if (col.type === 'server') {
      return <ServerPicker value={val} onChange={v => updateRow(idx, col.key, v)} servers={servers} placeholder={col.placeholder} />;
    }
    if (col.type === 'location') {
      return <LocationPicker value={val} onChange={v => updateRow(idx, col.key, v)} locations={locations} placeholder={col.placeholder} />;
    }
    if (col.type === 'checkbox') {
      return (
        <input type="checkbox" className="form-check-input"
          checked={!!val} onChange={e => updateRow(idx, col.key, e.target.checked)} />
      );
    }
    if (col.type === 'number') {
      return (
        <input type="number" className="form-control form-control-sm"
          value={val ?? ''} placeholder={col.placeholder || ''}
          onChange={e => updateRow(idx, col.key, e.target.value === '' ? null : Number(e.target.value))} />
      );
    }
    return (
      <input type="text" className="form-control form-control-sm"
        value={val || ''} placeholder={col.placeholder || ''}
        onChange={e => updateRow(idx, col.key, e.target.value)} />
    );
  };

  return (
    <div>
      {(!items || items.length === 0) && (
        <div style={{ fontSize: '0.78rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>
          {emptyText}
        </div>
      )}
      {editMode && (items || []).map((row, idx) => (
        <div key={idx} className="row g-2" style={{ alignItems: 'flex-end', marginBottom: 6 }}>
          {columns.map(col => (
            <div key={col.key} className="col" style={col.width ? { flex: `0 0 ${col.width}` } : {}}>
              <label className="form-label" style={{ fontSize: '0.7rem' }}>{col.label}</label>
              {renderInput(col, row, idx)}
            </div>
          ))}
          <div className="col-auto">
            <button className="dv-btn" type="button" onClick={() => removeRow(idx)} title="Eliminar">
              <TrashIcon style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
      ))}
      {!editMode && (items || []).length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                {columns.map(c => (
                  <th key={c.key} style={{ textAlign: 'left', padding: '4px 8px', color: TOPOLOGY_PALETTE.textMuted, borderBottom: '1px solid var(--slate-300)' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(items || []).map((row, idx) => (
                <tr key={idx}>
                  {columns.map(col => {
                    const val = row[col.key];
                    let display = val;
                    if (col.type === 'server' && val) display = serverLabel(val, servers);
                    else if (col.type === 'location' && val) display = locationLabel(val, locations);
                    else if (col.type === 'checkbox') display = val ? '✓' : '—';
                    return (
                      <td key={col.key} style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: TOPOLOGY_PALETTE.textStrong }}>
                        {display === '' || display == null ? '—' : display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editMode && (
        <button className="dv-btn" type="button" onClick={addRow} style={{ marginTop: 6 }}>
          <PlusIcon style={{ width: 14, height: 14 }} /> {addLabel}
        </button>
      )}
    </div>
  );
}

export function serverLabel(id, servers) {
  const s = (servers || []).find(x => x.id === Number(id));
  if (!s) return `#${id}`;
  return `${s.hostname || s.name}${s.ip ? ` · ${s.ip}` : ''}`;
}

export function locationLabel(id, locations) {
  const l = (locations || []).find(x => x.id === Number(id));
  if (!l) return `#${id}`;
  return `${l.name}${l.code ? ` (${l.code})` : ''}`;
}

export function SectionBox({ title, accent, children, right }) {
  return (
    <div style={{
      background: TOPOLOGY_PALETTE.cardBg,
      border: TOPOLOGY_PALETTE.cardBorder,
      borderRadius: 8, padding: 12,
      borderLeft: accent ? `4px solid ${accent}` : TOPOLOGY_PALETTE.cardBorder,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textStrong }}>
          {title}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function NodeCard({ icon: Icon, title, subtitle, badges, accent, footer }) {
  return (
    <div style={{
      background: TOPOLOGY_PALETTE.cardBg,
      border: accent ? `2px solid ${accent}` : TOPOLOGY_PALETTE.cardBorder,
      borderRadius: 8, padding: 10,
      minWidth: 180, maxWidth: 260,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {Icon && <Icon style={{ width: 16, height: 16, color: accent || TOPOLOGY_PALETTE.primaryAccent }} />}
        <strong style={{ fontSize: '0.85rem', color: TOPOLOGY_PALETTE.textStrong }}>{title}</strong>
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.72rem', color: TOPOLOGY_PALETTE.textMuted, marginBottom: 4 }}>
          {subtitle}
        </div>
      )}
      {Array.isArray(badges) && badges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: footer ? 4 : 0 }}>
          {badges.map((b, i) => (
            <span key={i} style={{
              background: b.bg || 'rgba(99,102,241,.18)',
              color: b.fg || '#a5b4fc',
              fontSize: '0.66rem', fontWeight: 700,
              borderRadius: 4, padding: '1px 6px',
              textTransform: 'uppercase', letterSpacing: '.04em',
            }}>{b.label}</span>
          ))}
        </div>
      )}
      {footer && (
        <div style={{ fontSize: '0.7rem', color: TOPOLOGY_PALETTE.textMuted, marginTop: 4 }}>
          {footer}
        </div>
      )}
    </div>
  );
}

export function useTopologyState(serviceId, companyId, defaultTopology) {
  const [topology, setTopology] = useState(defaultTopology);
  const [serverRefs,   setServerRefs]   = useState({});
  const [locationRefs, setLocationRefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [editMode, setEditMode] = useState(false);

  const reload = async () => {
    if (!serviceId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getServiceTopology(serviceId, companyId);
      setTopology(Object.keys(data.topology || {}).length ? data.topology : defaultTopology);
      setServerRefs(data.server_refs || {});
      setLocationRefs(data.location_refs || {});
    } catch (e) {
      setError(e.message || 'Error cargando topología');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [serviceId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.updateServiceTopology(serviceId, topology, companyId);
      setEditMode(false);
      await reload();
    } catch (e) {
      setError(e.message || 'Error guardando topología');
    } finally {
      setSaving(false);
    }
  };

  return {
    topology, setTopology,
    serverRefs, locationRefs,
    loading, error, saving,
    editMode, setEditMode,
    reload, save,
  };
}

export function TopologyHeader({ title, subtitle, editMode, saving, onSave, onCancel, onEdit, onReload, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
      <div style={{ flex: 1, minWidth: 280 }}>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: TOPOLOGY_PALETTE.textMuted, letterSpacing: '.06em' }}>
          {subtitle}
        </div>
        <h3 style={{ margin: '4px 0', color: TOPOLOGY_PALETTE.textStrong }}>{title}</h3>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {right}
        {onReload && (
          <button className="dv-btn" type="button" onClick={onReload} title="Recargar">Recargar</button>
        )}
        {editMode ? (
          <>
            <button className="dv-btn" type="button" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button className="dv-btn dv-btn-primary" type="button" onClick={onSave} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </>
        ) : (
          <button className="dv-btn dv-btn-primary" type="button" onClick={onEdit}>Editar</button>
        )}
      </div>
    </div>
  );
}

/**
 * Generic ListSection that wraps RowEditor with a header + accent.
 */
export function ListSection({ title, accent, badge, items, columns, editMode, onChange, servers, locations, addLabel, emptyText, defaultRow }) {
  return (
    <SectionBox
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {title}
          <span style={{
            background: TOPOLOGY_PALETTE.subtleBg, color: TOPOLOGY_PALETTE.textMuted,
            fontSize: '0.7rem', borderRadius: 4, padding: '0 6px',
          }}>
            {(items || []).length}
          </span>
          {badge}
        </span>
      }
      accent={accent}
    >
      <RowEditor
        items={items} onChange={onChange} columns={columns} editMode={editMode}
        servers={servers} locations={locations}
        addLabel={addLabel} emptyText={emptyText} defaultRow={defaultRow}
      />
    </SectionBox>
  );
}
